import { apiAnalytics } from "@/api/analytics/analytics.api.ts";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { RecruitingReportingButtonChooseGroupBy } from "@/pages/reporting/recruiting/components/RecruitingReportingButtonChooseGroupBy.tsx";
import { RecruitingReportingRecruitersSelector } from "@/pages/reporting/recruiting/components/RecruitingReportingRecruitersSelector.tsx";
import { ReportingValueModeSelector } from "@/pages/reporting/recruiting/components/ReportingValueModeSelector.tsx";
import { RecruitingStageFlowReportTable } from "@/pages/reporting/recruiting/stage-flow/RecruitingStageFlowReportTable.tsx";
import { StagesFunnelSelector } from "@/pages/reporting/recruiting/stage-flow/components/StagesFunnelSelector.tsx";
import { useRecruitAnalyticsStageFlow } from "@/queries/analytics/use-recruit-analytics.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiAnalyticsRecruitingFilter,
  ApiAnalyticsValueMode,
} from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ApiAggregationDate,
  Box,
  ButtonIcon,
  CoreFilterType,
  DropdownMenu,
  DropdownMenuItem,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  Stack,
} from "@skailer-com/uikit";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const RecruitingStageFlowReport = () => {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const [, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  useTitle(t("ats:report_hiring_pipeline"), t("common:reports"));
  const {
    filter: { filter },
    filterButton,
    config,
  } = useAppFilter<ApiAnalyticsRecruitingFilter>({
    filters: [
      {
        ...library.aggregation_date_mode,
        defaultValue: ApiAggregationDate.week,
      },
      {
        ...library.date_from,
        hideInFilter: true,
        name: "date_start",
      },
      {
        ...library.date_to,
        hideInFilter: true,
        name: "date_end",
      },
      {
        ...library.period,
        defaultValue: "last_week_4",
      },
      {
        ...library.employee_ids,
        name: "recruiter_ids",
        label: t("ats:recruiters"),
      },
      library.job_ids,
      library.department_ids,
      library.division_ids,
      {
        ...library.employee_ids,
        name: "stakeholder_ids",
        label: t("ats:stakeholders"),
      },
      {
        name: "group_by",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.string,
        renderType: FilterRenderType.none,
      },
      {
        name: "include_inactive_jobs",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.boolean,
        renderType: FilterRenderType.switch,
        label: t("reporting:include_inactive_jobs_title"),
        description: t("reporting:include_inactive_jobs_description"),
      },
      {
        name: "include_all_active",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.boolean,
        renderType: FilterRenderType.switch,
        label: t("reporting:include_all_active_title"),
        description: t("reporting:include_all_active_description"),
      },
      {
        name: "value_mode",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.string,
        renderType: FilterRenderType.none,
        defaultValue: ApiAnalyticsValueMode.total,
      },
      {
        name: "start_stage_id",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.number,
        renderType: FilterRenderType.none,
      },
      {
        name: "end_stage_id",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.number,
        renderType: FilterRenderType.none,
      },
    ],
    source: AnalyticReportTypes.recruitingStageFlow,
  });

  const { data: report, isLoading } = useRecruitAnalyticsStageFlow(filter);
  const { data: stages } = useStages(filter.value_mode === ApiAnalyticsValueMode.funnel);

  const { downloadFile } = useDownloadHelper();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.recruitingStageFlow,
    });
  });

  const handleExportClick = useCallback(() => {
    const filterToExport: ApiAnalyticsRecruitingFilter = {
      ...filter,
      format: "xlsx",
    };

    downloadFile({
      apiCall: () => apiAnalytics.recruiting.stageFlowReportExport(filterToExport),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.dataExported, {
          [analyticProperties.title]: AnalyticReportTypes.recruitingStageFlow,
        });
      },
    });
  }, [filter]);

  return (
    <>
      <Box className={"bg-light h-full overflow-y-hidden flex flex-col"}>
        <Page.Header title={t("ats:report_hiring_pipeline")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden flex-wrap"}>
            <ReportingValueModeSelector
              value={filter.value_mode ?? ApiAnalyticsValueMode.total}
              onChange={(mode) => {
                setSearchParams((searchParams) => {
                  searchParams.set("value_mode", mode);
                  if (mode !== ApiAnalyticsValueMode.funnel) {
                    searchParams.delete("start_stage_id");
                    searchParams.delete("end_stage_id");
                  }
                  return searchParams;
                });
              }}
            />
            <RecruitingReportingButtonChooseGroupBy
              value={filter.group_by}
              onChange={(group) => {
                setSearchParams((searchParams) => {
                  searchParams.set("group_by", group);
                  return searchParams;
                });
              }}
            />

            <FilterChoosePeriod
              availableAggregationDateModes={[
                ApiAggregationDate.year,
                ApiAggregationDate.quarter,
                ApiAggregationDate.month,
                ApiAggregationDate.week,
                ApiAggregationDate.day,
              ]}
              filtersConfig={config}
            />

            {filterButton()}
            {canIEdit(ApiPermission.reporting_export) && (
              <DropdownMenu
                trigger={<ButtonIcon icon={<FontAwesomeIcon icon={"fa-light fa-file-export"} />} />}
              >
                <DropdownMenuItem
                  onClick={handleExportClick}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                  title={t("common:export_to_xlsx")}
                />
              </DropdownMenu>
            )}
          </Stack>
        </Page.Header>

        <Page.Content className={"flex grow flex-col overflow-hidden"}>
          <Stack gap={"md"}>
            <Box>
              <RecruitingReportingRecruitersSelector
                recruiterIds={filter.recruiter_ids || []}
                onChange={(recruiterIds) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("recruiter_ids", recruiterIds.join(","));
                    return searchParams;
                  });
                }}
              />
            </Box>

            {filter.value_mode === ApiAnalyticsValueMode.funnel && stages && stages.length > 1 && (
              <StagesFunnelSelector
                stages={stages}
                startStageId={filter.start_stage_id}
                endStageId={filter.end_stage_id}
                onChange={(startStageId, endStageId) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("start_stage_id", startStageId.toString());
                    searchParams.set("end_stage_id", endStageId.toString());
                    return searchParams;
                  });
                }}
              />
            )}
          </Stack>

          <RecruitingStageFlowReportTable report={report} filter={filter} isLoading={isLoading} />
        </Page.Content>
      </Box>
    </>
  );
};
